import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function About() {
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const updateCartCount = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
        setCartCount(count);
      }
    };
    
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="bg-blue-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <span className="flex items-center">
                <i className="ri-phone-line mr-2"></i>
                +1 (929) 268-8770
              </span>
              <span className="flex items-center">
                <i className="ri-mail-line mr-2"></i>
                sam4104t@gmail.com
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="flex items-center">
                <i className="ri-truck-line mr-2"></i>
                Free Worldwide Shipping
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg mr-3">
                <i className="ri-medicine-bottle-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-blue-600">PillsForU</h1>
                <p className="text-xs text-gray-500">Trusted Healthcare</p>
              </div>
            </Link>

            <div className="flex items-center space-x-6">
              <Link to="/cart" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors cursor-pointer relative">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-2xl"></i>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{cartCount}</span>
                  )}
                </div>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">About PillsForU</h1>
          <p className="text-xl text-blue-100">Your Trusted Partner in Healthcare Since 2015</p>
        </div>
      </div>

      {/* Our Story */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
            <p className="text-gray-700 mb-4 leading-relaxed text-lg">
              Founded in 2015, PillsForU was born from a simple mission: to make quality healthcare accessible and affordable to everyone, everywhere. We recognized that millions of people worldwide struggle to access essential medications due to high costs and limited availability.
            </p>
            <p className="text-gray-700 mb-4 leading-relaxed text-lg">
              Today, we serve over 500,000 satisfied customers across 150+ countries, delivering genuine, FDA-approved medications at prices up to 70% lower than traditional pharmacies. Our commitment to quality, authenticity, and customer care has made us a trusted name in online healthcare.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              Every medication we dispense is sourced directly from licensed manufacturers and undergoes rigorous quality checks. Our team of licensed pharmacists is available 24/7 to provide expert guidance and ensure you receive the right treatment for your needs.
            </p>
          </div>
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/c5d530d656b9ca3df55b10d3049e5a83.jpg" 
              alt="Our Team" 
              className="rounded-2xl shadow-2xl object-cover w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-10 shadow-lg border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mb-6">
                <i className="ri-target-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                To provide safe, effective, and affordable medications to patients worldwide, breaking down barriers to healthcare access. We strive to deliver exceptional service, expert guidance, and genuine products that improve lives and promote wellness for all.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 shadow-lg border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-6">
                <i className="ri-eye-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                To become the world's most trusted online pharmacy, recognized for our unwavering commitment to quality, transparency, and patient care. We envision a future where everyone has access to the medications they need, regardless of location or financial circumstances.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
          <p className="text-lg text-gray-600">The principles that guide everything we do</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow border border-gray-200">
            <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-6">
              <i className="ri-shield-check-line text-4xl text-blue-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Quality &amp; Safety</h3>
            <p className="text-gray-600 leading-relaxed">Every medication is FDA-approved and sourced from licensed manufacturers. We never compromise on quality or authenticity.</p>
          </div>
          <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow border border-gray-200">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
              <i className="ri-heart-line text-4xl text-green-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Patient Care</h3>
            <p className="text-gray-600 leading-relaxed">Your health and wellbeing are our top priority. Our licensed pharmacists provide personalized support and guidance.</p>
          </div>
          <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow border border-gray-200">
            <div className="w-20 h-20 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-6">
              <i className="ri-hand-heart-line text-4xl text-purple-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Integrity</h3>
            <p className="text-gray-600 leading-relaxed">We operate with complete transparency and honesty. What you see is what you get - genuine products, fair prices, no hidden fees.</p>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="bg-blue-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Certifications &amp; Accreditations</h2>
            <p className="text-xl text-blue-100">Trusted and verified by leading healthcare authorities</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                <i className="ri-verified-badge-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">FDA Approved</h3>
              <p className="text-sm text-gray-600">All products certified</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">ISO Certified</h3>
              <p className="text-sm text-gray-600">Quality management</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-4">
                <i className="ri-lock-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">SSL Secured</h3>
              <p className="text-sm text-gray-600">Payment protection</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
                <i className="ri-award-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Licensed</h3>
              <p className="text-sm text-gray-600">Pharmacy certified</p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-5xl font-bold text-blue-600 mb-2">500K+</div>
            <p className="text-gray-600 font-semibold">Happy Customers</p>
          </div>
          <div>
            <div className="text-5xl font-bold text-green-600 mb-2">150+</div>
            <p className="text-gray-600 font-semibold">Countries Served</p>
          </div>
          <div>
            <div className="text-5xl font-bold text-purple-600 mb-2">1M+</div>
            <p className="text-gray-600 font-semibold">Orders Delivered</p>
          </div>
          <div>
            <div className="text-5xl font-bold text-orange-600 mb-2">24/7</div>
            <p className="text-gray-600 font-semibold">Customer Support</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Experience Better Healthcare?</h2>
          <p className="text-xl mb-8 text-blue-100">Join thousands of satisfied customers who trust PillsForU for their medication needs</p>
          <Link to="/all-products" className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer font-bold text-lg inline-block shadow-lg">
            Shop Now
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/all-products" className="hover:text-blue-400 transition-colors cursor-pointer">All Products</Link></li>
                <li><Link to="/contact" className="hover:text-blue-400 transition-colors cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions" className="hover:text-blue-400 transition-colors cursor-pointer">Terms &amp; Conditions</Link></li>
                <li><Link to="/refund-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Refund Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p>Phone: +1 (929) 268-8770</p>
              <p>Email: sam4104t@gmail.com</p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; 2024 PillsForU.com. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-blue-400 transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}